import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

import loginImage from "../../assets/images/lynn-dashed-background.png";
import { Button, Input, Popup, Select } from "../../components/workshop";
import RemoteLayout from "./layout";
import { WorkshopContext } from "../../contexts/workshop.context";
import { defaultPlatforms, defaultRoles } from "../../util/workshop.util";
import { toast } from "react-toastify";

const Login = ({ demoWorkshop = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({ name: "", role: "participant" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");
  const [disconnectedUsers, setDisconnctedUsers] = useState([]);
  const [confirmRejoinPopup, setConfirmRejoinPopup] = useState(false);

  const { socket, user, setUser, platform } = useContext(WorkshopContext);

  const { m, p, mp, id } = user || {};
  const tempRole = m ? t("moderator") : p ? t("participant") : t("moderatorAndParticipant");
  const baseKey = m ? "moderator" : "participant";

  const rejoinTranslations = t("rejoin", { returnObjects: true });

  const loginUser = async (e) => {
    e.preventDefault();

    setError("");

    console.log("userData", userData);
    if (userData.name) {
      socket.emit("login", userData);
      setLoggingIn(true);
    }
  };

  socket?.on("loginError", (error) => {
    setError(error);
    setLoggingIn(false);
  });

  socket?.on("loginSuccess", async (savedUserData) => {
    setUser({ m, p, mp, ...savedUserData });
    try {
      Cookies.set("userId", savedUserData?.id, { expires: 0.125 });
      navigate("/online/pre-ready");
    } catch (error) {
      console.log("Cookies unavailable");
    }
  });

  useEffect(() => {
    if (demoWorkshop) return;

    socket.emit("disconnectedUsers", (users) => {
      const tempUsers = mp
        ? users.filter((user) => user.role === defaultRoles.moderatorAndParticipant)
        : m
          ? users.filter((user) => user.role === defaultRoles.moderator)
          : users.filter((user) => user.role === "participant");
      setDisconnctedUsers(tempUsers);
    });

    socket.on("disconnectedUsers", (users) => {
      const tempUsers = mp
        ? users.filter((user) => user.role === defaultRoles.moderatorAndParticipant)
        : m
          ? users.filter((user) => user.role === defaultRoles.moderator)
          : users.filter((user) => user.role === "participant");
      setDisconnctedUsers(tempUsers);
    });
  }, []);

  useEffect(() => {
    if (demoWorkshop) return;
    if (p) {
      setUserData({ ...userData, role: defaultRoles.participant });
    }
    if (m) {
      setUserData({ ...userData, role: defaultRoles.moderator });
    }
    if (mp) {
      setUserData({ ...userData, role: defaultRoles.moderatorAndParticipant });
    }
  }, [p, m, mp]);

  useEffect(() => {
    let redirectUrl;

    if (id && !loggingIn) {
      // toast.info(`You are logged in as ${name} with role: ${role}. Redirecting to wait screen.`);
      redirectUrl = "/online/wait";
    } else {
      redirectUrl = "/online";
    }

    navigate(redirectUrl);
  }, [id]);

  return (
    <>
      <RemoteLayout>
        <div className="flex flex-col items-center justify-center text-white">
          <div className="mb-6 text-center">
            <h1 className="text-4xl font-semibold text-white-100">
              {t(`login.${baseKey}.welcomePrefix`)}{" "}
              <span className="text-orange-default">{t(`login.${baseKey}.missionTeam`)}</span>{" "}
              {t(`login.${baseKey}.welcomeSuffix`)}
            </h1>
            <p className="mt-2 text-white-100">{t(`login.${baseKey}.instructions`)}</p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-20 w-full max-w-4xl">
            <div className="w-80">
              <img
                src={loginImage}
                alt={t(`login.${baseKey}.robotMascot`)}
                className="w-full h-auto"
              />
            </div>

            {/* Form */}
            <div className="bg-orange-700 shadow-lg rounded-2xl p-10 w-full max-w-md">
              {disconnectedUsers.length > 0 ? (
                <>
                  {/* Rejoin Layout */}
                  <div className="mb-6">
                    <p className="text-orange-default font-semibold mb-3">
                      {t(`login.${baseKey}.yourRole`)}
                    </p>
                    <div className="flex items-center gap-2 mb-6">
                      <div
                        className={`w-10 h-10 flex items-center justify-center text-base font-semibold rounded-full text-white-100 ${
                          userData.role === defaultRoles.participant
                            ? "bg-orange-default"
                            : "bg-green-500"
                        }`}>
                        {tempRole?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-black-100 font-medium" dangerouslySetInnerHTML={{ __html: tempRole }} />
                    </div>

                    <Button
                      type="rounded"
                      className={`w-full font-medium mb-6 border-2 ${
                        userData.role === defaultRoles.participant
                          ? '!bg-orange-600 !text-orange-default border-orange-default'
                          : '!bg-green-50 !text-green-500 border-green-500'
                      }`}
                      onClick={() => {
                        if (disconnectedUsers.length === 1) {
                          // If only one disconnected user, go directly to confirmation
                          setConfirmRejoinPopup(disconnectedUsers[0].id);
                        } else {
                          // Multiple users, show selection
                          setConfirmRejoinPopup(true);
                        }
                      }}>
                      {t("preLogin.rejoinBtn")} →
                    </Button>
                  </div>

                  <div className="mt-6">
                    <p className="mb-2 text-gray-700">{t(`login.${baseKey}.namePrompt`)}</p>
                    <form onSubmit={loginUser}>
                      <Input
                        type="text"
                        placeholder={t(`login.${baseKey}.namePlaceholder`)}
                        value={userData.name}
                        onChange={(value) => setUserData({ ...userData, name: value })}
                      />
                    </form>
                    {error && <p className="text-red-500 text-sm mt-2">{t(error)}</p>}

                    <div className="flex items-center flex-col justify-center gap-2 my-3">
                      <Button
                        type="rounded"
                        className="w-full !bg-green-500 hover:!bg-green-600 !text-white-100 font-medium"
                        onClick={loginUser}>
                        {t(`login.${baseKey}.joinButton`) + " →"}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Normal Login Layout */}
                  <div className="mb-4">
                    <h2 className="text-center text-xl font-bold text-orange-default mb-4">
                      {t(`login.${baseKey}.yourRole`)}
                    </h2>
                    <div className="flex items-center justify-center gap-2">
                      <div
                        className={`w-7 h-7 flex items-center justify-center text-sm rounded-full text-black-100 text-center ${
                          userData.role === defaultRoles.participant
                            ? "bg-orange-default"
                            : "bg-green-500"
                        }`}>
                        {tempRole?.charAt(0)?.toUpperCase()}
                      </div>
                      <span dangerouslySetInnerHTML={{ __html: tempRole }} />
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="mb-2">{t(`login.${baseKey}.namePrompt`)}</p>
                    <form onSubmit={loginUser}>
                      <Input
                        type="text"
                        placeholder={t(`login.${baseKey}.namePlaceholder`)}
                        value={userData.name}
                        onChange={(value) => setUserData({ ...userData, name: value })}
                      />
                    </form>
                    {error && <p className="text-red-500 text-sm mt-2">{t(error)}</p>}

                    <div className="flex items-center flex-col justify-center gap-2 my-3">
                      <Button
                        type="rounded"
                        className={`w-full ${
                          userData.role === defaultRoles.participant
                            ? ""
                            : "!bg-green-500"
                        }`}
                        onClick={loginUser}>
                        {t(`login.${baseKey}.joinButton`) + " →"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </RemoteLayout>

      <Popup
        open={confirmRejoinPopup}
        setOpen={() => {
          if (confirmRejoinPopup === true) setConfirmRejoinPopup(false);
          else setConfirmRejoinPopup(true);
        }}
        containerStyle="!bg-orange-50"
        bottomPadding={false}
        hideOnActionBtnOnly={false}
        autoHeight={true}
        mobilePopupStyle={false}>
        {confirmRejoinPopup === true ? (
          <>
            <p
              className="text-xl text-orange-100 font-bold text-center mb-4 uppercase"
              dangerouslySetInnerHTML={{
                __html: t("rejoin.popup.selectNameHeading")
              }}
            />

            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();

                const targetUserId = e.target["rejoin-select"].value;
                setConfirmRejoinPopup(targetUserId);
              }}>
              <Select
                name={"rejoin-select"}
                placeholder={t("rejoin.popup.selectNamePlaceholder")}
                options={disconnectedUsers.map((du) => ({ value: du.id, label: du.name }))}
              />
              <Button
                className={`!rounded-full ${userData.role !== defaultRoles.participant && "!bg-green-500"}`}
                text={t("rejoin.popup.confirmSelection")}
                htmlType="submit"
              />
            </form>
          </>
        ) : (
          <>
            {rejoinTranslations?.popup?.heading && (
              <h2
                className="text-3xl font-bold text-center text-orange-100"
                dangerouslySetInnerHTML={{ __html: t("rejoin.popup.heading") }}
              />
            )}
            <p
              className="text-xl text-gray-100 text-center"
              dangerouslySetInnerHTML={{ __html: t("rejoin.popup.subheading") }}
            />

            <p className="text-3xl font-bold text-center text-gray-250 my-6">
              {disconnectedUsers?.find((du) => du.id === Number(confirmRejoinPopup))?.name}
            </p>

            <Button
              text={t("rejoin.popup.confirm")}
              className={`!rounded-full ${userData.role !== defaultRoles.participant && "!bg-green-500"}`}
              onClick={() =>
                socket.emit("rejoin", confirmRejoinPopup, (result) => {
                  if (!result) {
                    toast.error("Unable to join, try normal login.");
                    setConfirmRejoinPopup(false);
                    return;
                  }

                  setUser({ m, p, mp, ...result });

                  console.log("setting user", result);

                  // Persist the user so we can detect reconnection,
                  // but let PreReady handle the actual join & workshop sync.
                  Cookies.set("userId", result?.id); // session cookie, will expire when browser closes

                  setConfirmRejoinPopup(false);

                  if (platform === defaultPlatforms.mtOnline) {
                    // For online workshops, go through PreReady which requires user interaction
                    // before loading 100ms or joining the call.
                    navigate("/online/pre-ready");
                  } else {
                    // Keep existing behaviour for non-online workshops.
                    Cookies.set("setupCompleted", true); // session cookie, will expire when browser closes

                    socket.emit("syncToWorkshop", "", (url) => {
                      const redirectUrl =
                        url || (platform === defaultPlatforms.mtOnline ? "/online/wait" : "/wait");
                      navigate(redirectUrl);
                    });
                  }
                })
              }
            />
          </>
        )}
      </Popup>
    </>
  );
};

export default Login;
